import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TotalPairView } from '../../components/TotalPairView';
import { DashboardContext } from '../../contexts/DashboardContext';
import { IBillsTableRow } from '../../../BillPay/features/BillsHub/components/BillsTable/types';
import { VIEWS_MAP } from '../../../../router/constants';
import { WidgetLink } from '../../components/WidgetLink';
import { mapBillsToBillsTableRow } from '../../../BillPay/features/BillsHub/utils';
import { BillsTableRow } from '../../../BillPay/features/BillsHub/components/BillsTable/components/BillsTableRow';
import { formatValueWithoutCurrency } from '../../../../helpers/currency.helper';
import Heading from '../../../../generic-components/Heading';
import { useStore } from '../../../../store';
import { ReactComponent as BillsBlueIcon } from '../../../../assets/dashboard/bills-blue-icon.svg';
import { ReactComponent as BillsBlueV2Icon } from '../../../../assets/dashboard/bills-blue-v2-icon.svg';
import { InvoicesBillsWidgetsSkeleton } from '../../components/skeletons/InvoicesBillsWidgetsSkeleton';
import { trackMixPanel } from '../../../../services/mixpanel.service';

const columnKeys = ['billNumber', 'vendor', 'dueDate', 'calculatedStatus', 'amount'];

const testIdPrefix = 'BillsWidget';

const BILLS_WIDGET_STATES = {
  ZERO: 'ZERO',
  EMPTY: 'EMPTY',
  NOT_EMPTY: 'NOT_EMPTY',
};

const STYLES = {
  ZERO_STATE_BTN:
    'w-[170px] h-[32px] text-bw00 bg-action03 hover:bg-action04 active:bg-action05 rounded-[2px] shadow-widgetCard font-metricRSemibold text-[12px] flex justify-center items-center',
};

export const BillsWidget = ({ isLoading }: { isLoading: boolean }) => {
  const getSummaryData = useStore((state) => state.getSummaryData);
  const { lastBills, totalBillsDueAmount } = useContext(DashboardContext);
  const summary = getSummaryData();
  const {
    actionFlags: { billCreated },
  } = summary;
  const navigate = useNavigate();
  const { t } = useTranslation('dashboardFaceliftResourcesEN');
  const widgetState = billCreated
    ? lastBills?.length > 0
      ? BILLS_WIDGET_STATES.NOT_EMPTY
      : BILLS_WIDGET_STATES.EMPTY
    : BILLS_WIDGET_STATES.ZERO;
  const isZeroState = widgetState === BILLS_WIDGET_STATES.ZERO;
  const isEmptyState = widgetState === BILLS_WIDGET_STATES.EMPTY;
  const mappedBills = lastBills && mapBillsToBillsTableRow(lastBills.slice(0, 3));
  const handleBillRowClick = (billId: string) => {
    trackMixPanel('Act on Bills Widget', { Action: 'Act on Bill Pay Widget Row' });
    setTimeout(() => {
      navigate(`/${VIEWS_MAP.BILLS.BASE}?id=${billId}`);
    }, 300);
  };
  const renderBillsTable = () => (
    <table className='w-full' data-testid={`${testIdPrefix}__table`}>
      <tbody>
        {mappedBills?.map((row: IBillsTableRow, index: number) => (
          <BillsTableRow
            columnKeys={columnKeys}
            row={row}
            isLastRow={index === 2}
            key={`bill_${row.billId}`}
            lastRow={null}
            isWidgetMode
            onBillRowClick={handleBillRowClick}
          />
        ))}
      </tbody>
    </table>
  );
  const handleAddBillsToLiliClick = () => {
    trackMixPanel('Act on Bills Widget', { Action: 'Add your bills to Lili' });
    setTimeout(() => {
      navigate(`/${VIEWS_MAP.BILLS.BASE}/`);
    }, 300);
  };
  const renderEmptyState = () => (
    <div className='flex flex-col items-center justify-center h-full'>
      <BillsBlueIcon className='mt-[10px] w-[31px] h-[31px]' />
      <Heading
        title={t('BILLS_WIDGET_EMPTY_STATE_TITLE')}
        testId={`${testIdPrefix}__empty-state-title`}
        className='mt-[24px] text-bw08 font-metricR text-sm'
      />
    </div>
  );
  const renderZeroState = () => (
    <div className='flex flex-col items-center justify-center h-full'>
      <BillsBlueV2Icon className='mt-[32px] w-[31px] h-[31px]' />
      <Heading
        title={t('BILLS_WIDGET_ZERO_STATE_TITLE')}
        testId={`${testIdPrefix}__zero-state-title`}
        className='mt-[20px] text-bw08 font-metricRSemibold text-sm'
      />
      <Heading
        title={t('BILLS_WIDGET_ZERO_STATE_SUBTITLE')}
        testId={`${testIdPrefix}__zero-state-subtitle`}
        className='mt-[2px] text-bw08 font-metricR text-sm mb-[20px]'
      />
      <div className='mx-auto'>
        <button
          id={`${testIdPrefix}__btn-add-bills`}
          onClick={handleAddBillsToLiliClick}
          className={STYLES.ZERO_STATE_BTN}
          type='button'
          data-testid={`${testIdPrefix}__btn-add-bills`}
        >
          {t('BILLS_WIDGET_ZERO_STATE_BTN_TEXT')}
        </button>
      </div>
    </div>
  );
  if (isLoading) return <InvoicesBillsWidgetsSkeleton />;
  return (
    <div className='w-full h-full bg-bw00 pt-6 px-6 pb-0 rounded-[12px] flex flex-col'>
      <div className='flex justify-between w-full mb-[18px]'>
        <div className='font-metricRSemibold text-bw08 text-sm' data-testid={`${testIdPrefix}__title`}>
          {t('BILLS_WIDGET_TITLE')}
        </div>
        <div className='flex flex-row gap-[42px]'>
          <TotalPairView
            title={t('TOTAL_BILL_DUE')}
            value={formatValueWithoutCurrency(totalBillsDueAmount, 2)}
            testId={`${testIdPrefix}__totalBillDue`}
          />
        </div>
      </div>
      <div className={`h-[193px] border-bw03 ${isZeroState ? 'border-t-[1px]' : 'border-y-[1px]'}`}>
        {widgetState === BILLS_WIDGET_STATES.NOT_EMPTY && renderBillsTable()}
        {isZeroState && renderZeroState()}
        {isEmptyState && renderEmptyState()}
      </div>
      {!isZeroState && (
        <div className='h-[64px] w-full justify-end flex self-end'>
          <WidgetLink
            linkTitle={t('MANAGE_YOUR_BILLS')}
            linkUrl={`/${VIEWS_MAP.BILLS.BASE}`}
            testId={`${testIdPrefix}__link`}
            onClick={() => trackMixPanel('Act on Bills Widget', { Action: 'Manage your Bills' })}
          />
        </div>
      )}
    </div>
  );
};
